import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DOMPurify from 'dompurify';
import { Group, Vector2, Vector2Tuple } from 'three';

import { Html } from '@react-three/drei';
import { Billboard } from '@react-three/drei';
import { GroupProps, ThreeEvent } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';

import { SphereControlHandle } from '../SphereControlHandle/index.js';

import { AnnotationCanvasLabel } from './AnnotationCanvasLabel.js';
import { AnnotationHtmlLabel } from './AnnotationHtmlLabel.js';

export interface AnnotationControlsProps extends Omit<GroupProps, 'position'> {
  editable: boolean;
  outline: boolean;
  selector?: string;
  text?: string;
  position: Vector2Tuple;
  focus?: boolean;
  onPointChange?: (evt: {
    type: 'remove' | 'add' | 'update';
    last: boolean;
    point: Vector2;
  }) => void;
  onInputBlur?: (content: string) => void;
  onInputFocus?: () => void;
  onPointClick?: (event: ThreeEvent<MouseEvent>) => void;
  onInputChange?: (content: string) => void;
}

const _offset = new Vector2();
const _start = new Vector2();

export const AnnotationControls: React.FC<AnnotationControlsProps> = forwardRef<
  Group,
  AnnotationControlsProps
>(
  (
    {
      editable,
      outline,
      position,
      onPointChange,
      onInputFocus,
      onPointClick,
      onInputChange,
      onInputBlur,
      text,
      focus = false,
    },
    forwardedRef
  ) => {
    const [content, setContent] = useState('');
    const [textEditable, setTextEditable] = useState(false);
    const inputRef = useRef<HTMLDivElement>();

    useEffect(() => {
      if (text) setContent(text);
    }, []);

    useEffect(() => {
      if (editable && focus) {
        setTextEditable(true);
        inputRef.current?.focus();
      }
    }, [editable, focus]);

    const handleFocus = useEventCallback(() => {
      onInputFocus?.();
      setTextEditable(true);
    });

    const handleBlur = useEventCallback((evt: React.FocusEvent<HTMLDivElement>) => {
      const sanitizedHtml = DOMPurify.sanitize(evt.currentTarget.innerHTML);
      if (editable) {
        setContent(sanitizedHtml);
      }
      setTextEditable(false);
      onInputBlur?.(sanitizedHtml);
    });

    const handleInput = useEventCallback((inputContent: string) => {
      onInputChange?.(inputContent);
    });

    const handleClick = useEventCallback((event: ThreeEvent<MouseEvent>) => {
      onPointClick?.(event);
    });

    // eslint-disable-next-line no-unused-vars
    const handleDrag = useEventCallback(({ position, last, first: _ }) => {
      onPointChange?.({ type: 'update', last, point: position as Vector2 });
    });

    const handleLabelDrag = useEventCallback(({ position: dragPosition, last, first }) => {
      if (first) {
        _offset.set(position[0] - dragPosition.x, position[1] - dragPosition.y);
        _start.fromArray(position);
      } else {
        const newPosition = dragPosition.add(_offset) as Vector2;
        onPointChange?.({
          type: 'update',
          last,
          point: newPosition,
        });
        if (last && _start.distanceTo(newPosition) < 1) {
          if (editable) {
            inputRef.current?.focus();
          }
        }
      }
    });

    const setInputRef = useCallback(
      (node: HTMLDivElement) => {
        inputRef.current = node;
        if (editable && focus) {
          inputRef.current?.focus();
        }
      },
      [editable, focus]
    );

    const htmlLabel = useMemo(() => {
      return (
        <Html
          center
          style={{
            transform: 'translateY(calc(-100% - 15px)) translateX(-50%)',
            pointerEvents: textEditable ? 'auto' : 'none',
          }}
        >
          <AnnotationHtmlLabel
            ref={setInputRef}
            content={content}
            editable={editable}
            opacity={textEditable ? 1 : 0}
            outline={outline}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onInput={handleInput}
          />
        </Html>
      );
    }, [
      setInputRef,
      editable,
      handleFocus,
      handleBlur,
      handleInput,
      content,
      outline,
      textEditable,
    ]);

    const canvasLabel = useMemo(() => {
      return (
        <AnnotationCanvasLabel content={content} draggable={editable} onDrag={handleLabelDrag} />
      );
    }, [content, editable, handleLabelDrag]);

    const pointIndicator = useMemo(() => {
      return (
        <group>
          <SphereControlHandle
            color={outline ? 'orange' : 'white'}
            draggable={editable}
            hoverCursor={editable ? 'move' : 'pointer'}
            outline={outline}
            onClick={handleClick}
            onDrag={handleDrag}
          />
        </group>
      );
    }, [outline, editable, handleClick, handleDrag]);

    return (
      <group ref={forwardedRef} position={[position[0], 0, position[1]]}>
        <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>
          {!textEditable && <group renderOrder={1008}>{pointIndicator}</group>}
          {!textEditable && <>{canvasLabel}</>}
        </Billboard>
        <>{htmlLabel}</>
      </group>
    );
  }
);
